import moment from "moment";
import { IGetDateDefaulParams, ISeasonInfo } from "../types/dashboard";

/**
 * @description: 获取接口补齐日期时间
 * @param {string} timeType 日期粒度
 * @param {number} rangeTime 起始日期的值
 * @param {number} dateFilterIsRange 筛选器展示是否是多天，多天则不用前推直接返回原时间范围
 * @return {[moment.Moment, moment.Moment]} 返回默认日期时间
 */
export const getDefaultUtcDateValue = (timeType: string, rangeTime: string[], dateFilterIsRange?: boolean) => {
  const [start, end] = rangeTime || [];
  if (timeType?.toLocaleLowerCase() === 'week') {
    return [
      moment(start)
        .subtract(5, 'weeks')
        .startOf('isoWeek')
        .format('YYYY-MM-DD'),
      end
    ];
  }
  if (timeType?.toLocaleLowerCase() === 'month') {
    return [
      moment(start)
        .subtract(5, 'months')
        .startOf('month')
        .format('YYYY-MM-DD'),
      end
    ];
  }
  return dateFilterIsRange ? rangeTime : [moment(start).subtract(13, 'day').format('YYYY-MM-DD'), end];
};

/**
 * @description: 获取时间筛选器默认值
 * @param {string} timeType 默认日期粒度：当前支持日、周、月
 * @param {number} timeZone 后端配置的游戏时区
 * @param {number} startNum 开始时间T - startNum
 * @param {number} endNum 结束时间T - endNum
 * @param {number} singleNum 单时间 T - singleNum
 * @param {boolean} isSingle 是否是单粒度，当前用在夜莺的日报
 * @return {[moment.Moment, moment.Moment]} 返回筛选器默认日期
 */
export const getDateDefault = (params: IGetDateDefaulParams): [moment.Moment, moment.Moment] | string => {
  // todo 待确认：是否可以用 singleNum 代替isSingle
  const { timeType = 'day', timeZone, isSingle = false, startNum = 1, endNum = 1, singleNum = 1, isLastDateMonth = false, lastDateStartNum = 1, lastDateEndNum = 1 } = params || {};
  // 用两个变量是因为起始值如果进行了subtract，会改变原有值，如果结束的值用同一个变量，会产生深拷贝问题
  // 偏移时区过后格式转换，解决week粒度下23:59:59TZ被转换成第二天
  const currentStartDate = moment(moment().utcOffset(timeZone).format('YYYY-MM-DD HH:mm:ss')).startOf('day');
  const currentEndDate = moment(moment().utcOffset(timeZone).format('YYYY-MM-DD HH:mm:ss')).startOf('day');
  if (timeType?.toLocaleLowerCase() === 'week') {
    // 不存在week和month的isSingle场景
    // startOf('day') 精确到该月第一天的0点，防止hour影响造成误差
    return [
      currentStartDate.subtract(startNum, 'weeks').startOf('isoWeek').startOf('day'),
      currentEndDate.subtract(endNum, 'weeks').endOf('isoWeek').startOf('day')
    ];
  }

  if (timeType?.toLocaleLowerCase() === 'month') {
    if (isLastDateMonth) {
      const monthStartVal = currentStartDate.subtract(lastDateStartNum, 'days');
      const monthEndVal = currentEndDate.subtract(lastDateEndNum, 'days');
      return [monthStartVal.startOf('month').startOf('day'), monthEndVal.endOf('month').startOf('day')];
    }
    return [
      currentStartDate.subtract(startNum, 'months').startOf('month').startOf('day'),
      currentEndDate.subtract(endNum, 'months').endOf('month').startOf('day')
    ];
  }

  return isSingle ? currentStartDate.subtract(singleNum, 'days').format('YYYY-MM-DD')
    : [currentStartDate.subtract(startNum, 'days'), currentEndDate.subtract(endNum, 'days')];
};

export const getLastDate = (timeZone: number) => getDateDefault({ timeType: 'day', timeZone, isSingle: true, singleNum: 1 });


/**
 * @description: 大部分日周月页面的默认日期配置
 * @param {number} timeZone 后端配置的游戏时区
 * @param {ISeasonInfo} seasonInfo 后端配置的事件相关信息
 * @param {string[]} typeList 结束时间T - endNum
 */
export const commonDateDefault = (timeZone: number, seasonInfo?: ISeasonInfo, typeList?: string[], dateOffset?: any) => {
  // 可配置日期偏移量，默认日T-1+偏移14天，周T-1+偏移6周，月T-1+偏移6天
  const { dayStartOffset = 14, weekStartOffset = 6, monthStartOffset = 6,
    dayEndOffset = 1, weekEndOffset = 1, monthEndOffset = 1, } = dateOffset || {};

  const mappedTypeList = typeList
    ? (typeList || []).reduce((acc: string[], t) => {
      acc.push(t === 'day' ? 'date' : t);
      return acc;
    }, [])
    : undefined;

  const defaultValues = {
    date: getDateDefault({ timeType: 'day', timeZone, startNum: dayStartOffset, endNum: dayEndOffset }),
    week: getDateDefault({ timeType: 'week', timeZone, startNum: weekStartOffset, endNum: weekEndOffset }),
    month: getDateDefault({ timeType: 'month', timeZone, startNum: monthStartOffset, endNum: monthEndOffset }),
  };

  const defaultType = (mappedTypeList && mappedTypeList.length > 0) ? mappedTypeList[0] : 'date';
  return {
    type: 'rangeDate',
    key: 'rangeTime',
    default: {
      type: defaultType,
      value: defaultValues[defaultType as keyof typeof defaultValues] || defaultValues.date,
    },
    componentDefault: defaultValues,
    typeList: mappedTypeList || ['date', 'week', 'month'],
    ...(seasonInfo ? { extraShortcut: seasonInfo } : {})
  };
};